/**
 * utils/businesses.js
 * Pure functions for the businesses collection (Contact model redesign). A Business is the
 * real-world firm that sits ABOVE contact entries — a PUSAT entry and a CABANG entry (or several
 * entries in either list) can point at the same Business via `contact.businessId` to mean "this
 * is the same real firm," e.g. a firm with a shop and a separate warehouse in one city is two
 * CABANG entries and one Business. Nothing in this module (or anywhere else this stage) ever
 * infers or creates that link — matching an entry to a Business is always a deliberate human
 * action, elsewhere.
 * `data.businesses`: { id, name, note, createdAt }[]. Carries as little as possible — every real
 * detail (address, NPWP, phone, ekspedisi, ...) lives on the contact entries themselves, not
 * here.
 *
 * Mirrors persons.js's own shape and its ONE deliberate divergence from contacts.js: no name-
 * collision check. Two different real firms can share a display name (a Pusat entry's business
 * name is the legal entity; a Cabang entry's is the shop name — they can differ for the SAME
 * business too), so uniqueness would fight the data rather than model it — same reasoning
 * persons.js already states for people. `note` exists so a human can tell two same-named
 * businesses apart.
 */
import { generateId } from "./idGenerators";

function norm(s) {
  return String(s || "").trim();
}

/** Add a new business. Same-reference no-op when `fields.name` is blank after trim — no other
 *  validation, and deliberately no name-collision check (see file header). */
export function addBusiness(data, fields) {
  const name = norm(fields?.name);
  if (!name) return data;
  const businesses = data.businesses || [];
  const business = { id: generateId(), name, note: norm(fields?.note), createdAt: new Date().toISOString() };
  return { ...data, businesses: [...businesses, business] };
}

/** Update an existing business's name/note. Same-reference no-op when `id` doesn't resolve or
 *  the new name is blank. An empty note is a valid edit (clearing it), never a rejection. */
export function updateBusiness(data, id, fields) {
  const businesses = data.businesses || [];
  const idx = businesses.findIndex((b) => b.id === id);
  if (idx === -1) return data;
  const current = businesses[idx];
  const name = fields?.name != null ? norm(fields.name) : current.name;
  if (!name) return data;
  const note = fields?.note != null ? norm(fields.note) : current.note;
  const next = { ...current, name, note };
  return { ...data, businesses: businesses.map((b, i) => (i === idx ? next : b)) };
}

/** Whether a businessId resolves against data.businesses — used by contacts.js's own
 *  `validateContactFields` (a contact's businessId, if set, must point at a real business). */
export function businessExists(businesses, id) {
  return (businesses || []).some((b) => b.id === id);
}

/** Number of contact entries (data.contacts, either list) pointing at this business via
 *  businessId. */
export function businessRefCount(data, id) {
  return (data.contacts || []).filter((c) => c.businessId === id).length;
}

/** Every contact entry currently linked to this business — the building block reporting needs
 *  to break a business total back down to its individual entries. A business may legitimately
 *  hold several entries, including more than one in the SAME list (e.g. a shop and a separate
 *  warehouse for the same firm, both CABANG) — this returns each one, never a collapsed total,
 *  so a caller can never mistake two real locations for one. */
export function contactsForBusiness(data, businessId) {
  return (data.contacts || []).filter((c) => c.businessId === businessId);
}

/** Delete only when unreferenced (businessRefCount === 0). A business orphaned by its last
 *  contact entry being deleted/unlinked is NOT auto-deleted — every other record in this app
 *  (a person with zero roles, a category with zero items) survives until a human explicitly
 *  removes it, and a Business follows the same rule. This is the guarded manual path a future
 *  business-management screen would call. */
export function deleteBusiness(data, id) {
  if (businessRefCount(data, id) > 0) return data;
  const businesses = data.businesses || [];
  const next = businesses.filter((b) => b.id !== id);
  if (next.length === businesses.length) return data;
  return { ...data, businesses: next };
}

/** Display label for a business: name, plus an optional " — {note}" suffix when set. */
export function displayBusiness(b) {
  const note = norm(b?.note);
  return note ? `${b.name} — ${note}` : b.name;
}
